import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EvaluationLayout from '../../components/EvaluationLayout';
import {
  BarChart3,
  Plus,
  Calendar,
  TrendingUp,
  Search,
  Filter,
  Play,
  Pause,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Activity,
  Mail,
  Send,
  Trash2
} from 'lucide-react';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Badge from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { formatDate } from '../../lib/utils';
import evaluationService from '../../services/evaluationService';
import { toast } from 'react-hot-toast';
import ScheduleModal from '../../components/ScheduleModal';
import SendEmailModal from '../../components/SendEmailModal';
import SendWebhookModal from '../../components/SendWebhookModal';

const EvaluationsDashboard = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('evaluations');

  // Evaluations state
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    scheduled: 0,
    avgPerformance: 0
  });

  // Runs state
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsSearchTerm, setRunsSearchTerm] = useState('');
  const [runsFilterStatus, setRunsFilterStatus] = useState('all');
  const [selectedRun, setSelectedRun] = useState(null);
  const [showRunDetailModal, setShowRunDetailModal] = useState(false);

  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [modalRunData, setModalRunData] = useState(null);

  // No mock data - users start with clean slate

  const location = useLocation();

  useEffect(() => {
    loadEvaluations();
    if (activeTab === 'runs') {
      loadRuns();
    }
  }, [activeTab, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update stats whenever evaluations change
  useEffect(() => {
    loadStats();
  }, [evaluations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync status filter from URL (?status=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    const allowed = ['all', 'running', 'completed', 'scheduled', 'failed', 'paused'];
    if (statusParam && allowed.includes(statusParam)) {
      setFilterStatus(statusParam);
    } else {
      setFilterStatus('all');
    }
  }, [location.search]);

  const loadEvaluations = async () => {
    try {
      setLoading(true);
      const response = await evaluationService.getEvaluations({ per_page: 50 }, { cache: false });

      let evaluationData = [];
      // Robustly handle various response shapes
      if (Array.isArray(response)) {
        evaluationData = response;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        evaluationData = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        evaluationData = response.data;
      } else if (response?.success && Array.isArray(response.success)) {
        evaluationData = response.success;
      } else if (response?.evaluations && Array.isArray(response.evaluations)) {
        evaluationData = response.evaluations;
      }

      // Normalize for UI convenience
      const normalized = (evaluationData || []).map((e) => {
        const latestResult = e.latestResult || e.latest_result || null;
        return {
          ...e,
          latestResult,
          lastRun: e.last_run_at || e.lastRun || latestResult?.created_at || null,
        };
      });

      if (!Array.isArray(normalized)) {
        throw new Error('Invalid data format');
      }

      setEvaluations(normalized);
    } catch (error) {
      console.error('Failed to load evaluations:', error);
      toast.error('Failed to load evaluations');
      // Fallback to localStorage
      try {
        const savedEvaluations = localStorage.getItem('evaluations');
        const evaluations = savedEvaluations ? JSON.parse(savedEvaluations) : [];
        setEvaluations(evaluations);
      } catch (e) {
        setEvaluations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Calculate stats from the actual evaluations data
      const scheduled = evaluations.filter(e => e.status === 'scheduled').length;
      const completed = evaluations.filter(e => e.status === 'completed').length;
      const avgPerformance = completed > 0 ? 85 : 0; // Mock average for now

      setStats({
        total: evaluations.length,
        scheduled: scheduled,
        avgPerformance: avgPerformance
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
      setStats({
        total: 0,
        scheduled: 0,
        avgPerformance: 0
      });
    }
  };

  const loadRuns = async () => {
    try {
      setRunsLoading(true);
      console.log('Dashboard: loadRuns called');
      const response = await evaluationService.getEvaluationRuns({ per_page: 50 }, { cache: false });
      console.log('Dashboard: loadRuns response:', response);
      console.log('Dashboard: response type:', typeof response);
      console.log('Dashboard: response.success:', response?.success);
      console.log('Dashboard: response.data:', response?.data);

      // Check if the response indicates an error
      if (response?.success === false) {
        throw new Error(response.error || response.message || 'API returned error');
      }

      let runsData = [];
      // Robustly handle various response shapes
      if (Array.isArray(response)) {
        runsData = response;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        runsData = response.data.data;
        console.log('Dashboard: using response.data.data, length:', runsData.length);
      } else if (response?.data && Array.isArray(response.data)) {
        runsData = response.data;
        console.log('Dashboard: using response.data, length:', runsData.length);
      } else if (response?.success && Array.isArray(response.success)) {
        runsData = response.success;
      } else if (response?.runs && Array.isArray(response.runs)) {
        runsData = response.runs;
      } else {
        console.warn('Dashboard: Unexpected response structure:', response);
      }

      // Normalize for UI convenience
      const normalized = (runsData || []).map((run) => {
        return {
          ...run,
          evaluation_name: run.evaluation_name || run.evaluation?.name || 'Unknown Evaluation',
          model: run.model || run.execution_details?.model || run.evaluation?.model || 'N/A',
          provider: run.provider || run.execution_details?.provider || run.evaluation?.provider || 'N/A',
          started_at: run.started_at || run.execution_details?.started_at || run.created_at,
          completed_at: run.completed_at || run.execution_details?.completed_at || run.updated_at,
          overall_score: run.overall_score || run.results?.summary?.average_score,
          total_tests: run.total_tests || run.results?.summary?.total_tests,
          passed_tests: run.passed_tests || run.results?.summary?.passed_tests,
          failed_tests: run.failed_tests || run.results?.summary?.failed_tests,
          progress: run.progress || { percentage: 0, completed_tests: 0, total_tests: 0 },
        };
      });

      if (!Array.isArray(normalized)) {
        throw new Error('Invalid data format');
      }

      console.log('Dashboard: normalized runs:', normalized);
      setRuns(normalized);
    } catch (error) {
      console.error('Failed to load runs:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });

      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else {
        toast.error('Failed to load evaluation runs: ' + (error.message || 'Unknown error'));
      }

      // Fallback to empty array
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  };

  const handleRunEvaluation = async (evaluationId) => {
    try {
      toast.loading('Starting evaluation...', { id: 'run-eval' });

      // Optimistic update - show running state immediately
      setEvaluations(prev => prev.map(evaluation =>
        evaluation.id === evaluationId
          ? { ...evaluation, status: 'running', progress: 0 }
          : evaluation
      ));

      const response = await evaluationService.executeEvaluation(evaluationId, {
        realtime: true,
        progress_tracking: true
      });

      if (response?.success) {
        toast.success('Evaluation started successfully', { id: 'run-eval' });

        // Navigate to the execution page to see progress
        navigate(`/evaluations/${evaluationId}/execute`);
      } else {
        // Revert optimistic update on failure
        await loadEvaluations();
        const errorMessage = response?.error || response?.message || 'Failed to start evaluation';
        toast.error(errorMessage, { id: 'run-eval' });
      }
    } catch (error) {
      // Revert optimistic update on error
      await loadEvaluations();
      console.error('Failed to run evaluation:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      toast.error(`Failed to start evaluation: ${errorMessage}`, { id: 'run-eval' });
    }
  };

  const handlePauseEvaluation = async (evaluationId) => {
    try {
      // TODO: Replace with actual API call
      // await axios.post(`/api/evaluations/${evaluationId}/pause`);

      // Mock implementation
      setEvaluations(prev => prev.map(evaluation =>
        evaluation.id === evaluationId
          ? { ...evaluation, status: 'paused' }
          : evaluation
      ));
    } catch (error) {
      console.error('Failed to pause evaluation:', error);
    }
  };

  const handleScheduleFromRun = (run) => {
    setModalRunData(run);
    setShowScheduleModal(true);
  };

  const handleSendEmail = (run) => {
    setModalRunData(run);
    setShowEmailModal(true);
  };

  const handleSendWebhook = (run) => {
    setModalRunData(run);
    setShowWebhookModal(true);
  };

  const onScheduleSubmit = async (scheduleData) => {
    try {
      const response = await evaluationService.scheduleFromRun(modalRunData.run_id, scheduleData);

      if (response.success) {
        toast.success('Evaluation scheduled successfully');
      } else {
        toast.error(response.error || 'Failed to schedule evaluation');
      }
    } catch (error) {
      console.error('Failed to schedule from run:', error);
      toast.error('Failed to schedule evaluation');
      throw error;
    }
  };

  const onEmailSubmit = async (emailData) => {
    try {
      const response = await evaluationService.sendRunEmail(modalRunData.run_id, emailData);

      if (response.success) {
        toast.success('Email sent successfully');
      } else {
        toast.error(response.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email');
      throw error;
    }
  };

  const onWebhookSubmit = async (webhookData) => {
    try {
      const response = await evaluationService.sendRunWebhook(modalRunData.run_id, webhookData);

      if (response.success) {
        toast.success('Webhook sent successfully');
      } else {
        toast.error(response.error || 'Failed to send webhook');
      }
    } catch (error) {
      console.error('Failed to send webhook:', error);
      toast.error('Failed to send webhook');
      throw error;
    }
  };

  const handleCancelRun = async (run) => {
    try {
      if (window.confirm('Are you sure you want to cancel this evaluation run?')) {
        const response = await evaluationService.cancelRun(run.run_id);

        if (response.success) {
          toast.success('Evaluation run cancelled successfully');
          // Refresh runs to show updated status
          loadRuns();
          // Close modal if it's open
          setShowRunDetailModal(false);
          setSelectedRun(null);
        } else {
          toast.error(response.error || 'Failed to cancel evaluation run');
        }
      }
    } catch (error) {
      console.error('Failed to cancel run:', error);
      toast.error('Failed to cancel evaluation run');
    }
  };

  const handleDeleteEvaluation = async (evaluationId) => {
    try {
      if (window.confirm('Are you sure you want to delete this evaluation? This will also delete all associated runs and cannot be undone.')) {
        toast.loading('Deleting evaluation...', { id: 'delete-eval' });

        // Optimistically remove from UI
        setEvaluations(prev => prev.filter(e => e.id !== evaluationId));

        const response = await evaluationService.deleteEvaluation(evaluationId);

        if (response?.success) {
          toast.success('Evaluation deleted successfully', { id: 'delete-eval' });
          // Close modal if it's open
          setShowDetailModal(false);
          setSelectedEvaluation(null);
          // Reload to ensure consistency
          await loadEvaluations();
        } else {
          // Revert optimistic update on failure
          await loadEvaluations();
          const errorMessage = response?.error || response?.message || 'Failed to delete evaluation';
          toast.error(errorMessage, { id: 'delete-eval' });
        }
      }
    } catch (error) {
      console.error('Failed to delete evaluation:', error);
      // Revert optimistic update on error
      await loadEvaluations();
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      toast.error(`Failed to delete evaluation: ${errorMessage}`, { id: 'delete-eval' });
    }
  };



  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-gray-500" />;
      case 'active':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      running: 'default',
      completed: 'secondary',
      failed: 'destructive',
      scheduled: 'outline',
      paused: 'outline',
      active: 'default'
    };

    return (
      <Badge variant={variants[status] || 'outline'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getLatestRunBadge = (evaluation) => {
    const latestStatus = evaluation?.latestResult?.status;
    if (!latestStatus) return null;
    const variant = latestStatus === 'success' ? 'success' : (latestStatus === 'failed' ? 'destructive' : 'secondary');
    const label = latestStatus.charAt(0).toUpperCase() + latestStatus.slice(1);
    const Icon = latestStatus === 'success' ? CheckCircle : (latestStatus === 'failed' ? XCircle : AlertCircle);
    return (
      <span className="inline-flex items-center gap-1">
        <Icon className={`w-4 h-4 ${latestStatus === 'success' ? 'text-green-500' : latestStatus === 'failed' ? 'text-red-500' : 'text-muted-foreground'}`} />
        <Badge variant={variant}>{label}</Badge>
      </span>
    );
  };

  const getScheduleDisplay = (schedule) => {
    if (!schedule) return 'One-time';
    if (schedule.enabled === false) return 'Disabled';
    const freq = (schedule.frequency || 'custom').toString();
    let display = freq.charAt(0).toUpperCase() + freq.slice(1);
    if (freq === 'weekly' && schedule.day_of_week !== undefined) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      display += ` (${days[schedule.day_of_week]})`;
    } else if (freq === 'monthly' && schedule.day_of_month !== undefined) {
      const j = schedule.day_of_month % 10;
      const k = schedule.day_of_month % 100;
      const suffix = (j === 1 && k !== 11) ? 'st' : (j === 2 && k !== 12) ? 'nd' : (j === 3 && k !== 13) ? 'rd' : 'th';
      display += ` (${schedule.day_of_month}${suffix})`;
    }
    if (schedule.time) {
      display += ` at ${schedule.time}`;
    }
    return display;
  };

  const filteredEvaluations = evaluations.filter(evaluation => {
    const matchesSearch = evaluation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evaluation.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || evaluation.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const filteredRuns = runs.filter(run => {
    const evaluationName = run.evaluation_name || run.evaluation?.name || 'Unknown';
    const runId = run.run_id || '';

    const matchesSearch = evaluationName.toLowerCase().includes(runsSearchTerm.toLowerCase()) ||
      runId.toLowerCase().includes(runsSearchTerm.toLowerCase());
    const matchesFilter = runsFilterStatus === 'all' || run.status === runsFilterStatus;
    return matchesSearch && matchesFilter;
  });

  const RunDetailModal = ({ run, isOpen, onClose }) => {
    if (!isOpen || !run) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Run Details</h2>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-medium mb-3">Run Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Run ID</p>
                  <p className="font-medium">{run.run_id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Evaluation</p>
                  <p className="font-medium">{run.evaluation_name || run.evaluation?.name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Model</p>
                  <p className="font-medium">{run.model}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Provider</p>
                  <p className="font-medium">{run.provider}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Trigger</p>
                  <p className="font-medium">{run.trigger}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(run.status)}
                    {getStatusBadge(run.status)}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress */}
            {run.status === 'running' && run.progress && (
              <div>
                <h3 className="text-lg font-medium mb-3">Progress</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Tests completed</span>
                    <span>{run.progress.completed_tests}/{run.progress.total_tests}</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${run.progress.percentage}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">{run.progress.percentage}% complete</p>
                </div>
              </div>
            )}

            {/* Results */}
            {run.status === 'completed' && (
              <div>
                <h3 className="text-lg font-medium mb-3">Results</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <p className="text-2xl font-bold text-foreground">
                      {run.results?.summary?.average_score !== undefined
                        ? Math.round(run.results.summary.average_score * 100)
                        : 'N/A'}%
                    </p>
                    <p className="text-sm text-muted-foreground">Overall Score</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {run.results?.summary?.passed_tests ?? 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Passed</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {run.results?.summary?.failed_tests ?? 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Failed</p>
                  </div>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div>
              <h3 className="text-lg font-medium mb-3">Timeline</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Started</span>
                  <span className="text-sm">{formatDate(run.started_at)}</span>
                </div>
                {run.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Completed</span>
                    <span className="text-sm">{formatDate(run.completed_at)}</span>
                  </div>
                )}
                {run.duration_ms && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Duration</span>
                    <span className="text-sm">{Math.round(run.duration_ms / 1000)}s</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-border flex justify-between">
            <div className="flex space-x-3">
              {run.status === 'running' && (
                <Button
                  variant="destructive"
                  onClick={() => handleCancelRun(run)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Run
                </Button>
              )}
              {run.status === 'completed' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleScheduleFromRun(run)}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleSendEmail(run)}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleSendWebhook(run)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Webhook
                  </Button>
                </>
              )}
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {run.status === 'completed' && (
                <Button onClick={() => window.location.href = `/evaluations/runs/${run.run_id}`}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Full Results
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EvaluationDetailModal = ({ evaluation, isOpen, onClose }) => {
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    useEffect(() => {
      const loadLogs = async () => {
        if (!isOpen || !evaluation?.id) return;
        setLogsLoading(true);
        try {
          const params = {};
          if (evaluation?.latestResult?.run_id) params.run_id = evaluation.latestResult.run_id;
          const resp = await evaluationService.getEvaluationLogs(evaluation.id, { per_page: 100, ...params });
          let items = [];
          if (Array.isArray(resp)) items = resp;
          else if (resp?.data?.data && Array.isArray(resp.data.data)) items = resp.data.data;
          else if (resp?.data && Array.isArray(resp.data)) items = resp.data;
          setLogs(items);
        } catch (e) {
          console.error('Failed to load logs', e);
        } finally {
          setLogsLoading(false);
        }
      };
      loadLogs();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, evaluation?.id, evaluation?.latestResult?.run_id]);

    if (!isOpen || !evaluation) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">{evaluation.name}</h2>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-medium mb-3">Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Provider</p>
                  <p className="font-medium">{evaluation.provider}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Model</p>
                  <p className="font-medium">{evaluation.model}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Template</p>
                  <p className="font-medium">{evaluation.template?.name || evaluation.template || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Schedule</p>
                  <p className="font-medium">{getScheduleDisplay(evaluation.schedule)}</p>
                </div>
              </div>
            </div>

            {/* Status and Progress */}
            <div>
              <h3 className="text-lg font-medium mb-3">Status</h3>
              <div className="flex items-center space-x-4 mb-4">
                {getStatusIcon(evaluation.status)}
                {getStatusBadge(evaluation.status)}
                {getLatestRunBadge(evaluation)}
                {evaluation.status === 'running' && (
                  <span className="text-sm text-muted-foreground">
                    {evaluation.progress}% complete
                  </span>
                )}
              </div>

              {evaluation.status === 'running' && (
                <div className="w-full bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${evaluation.progress}%` }}
                  />
                </div>
              )}
            </div>

            {/* Results */}
            {evaluation.results && (
              <div>
                <h3 className="text-lg font-medium mb-3">Latest Results</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <p className="text-2xl font-bold text-foreground">{evaluation.results.accuracy}%</p>
                    <p className="text-sm text-muted-foreground">Accuracy</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <p className="text-2xl font-bold text-foreground">{evaluation.results.responseTime}s</p>
                    <p className="text-sm text-muted-foreground">Response Time</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/50 rounded-lg">
                    <p className="text-2xl font-bold text-foreground">${evaluation.results.cost}</p>
                    <p className="text-sm text-muted-foreground">Cost</p>
                  </div>
                </div>
              </div>
            )}

            {/* Logs */}
            <div>
              <h3 className="text-lg font-medium mb-3">Logs</h3>
              {logsLoading ? (
                <div className="text-sm text-muted-foreground">Loading logs...</div>
              ) : logs.length === 0 ? (
                <div className="text-sm text-muted-foreground">No logs available</div>
              ) : (
                <div className="max-h-80 overflow-auto rounded border border-border">
                  {logs.map((log) => {
                    const meta = log.metadata || {};
                    const ts = log.created_at || meta.timestamp;
                    const step = meta.step || 'event';
                    const status = meta.status || 'info';
                    return (
                      <div key={log.id} className="p-2 text-sm flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant={status === 'success' ? 'success' : status === 'failure' ? 'destructive' : 'secondary'}>
                            {step}
                          </Badge>
                          <span className="text-muted-foreground">{meta.error || meta.message || ''}</span>
                        </div>
                        <span className="text-muted-foreground">{ts ? formatDate(ts) : ''}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Error Message */}
            {evaluation.error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive font-medium">Error</p>
                <p className="text-sm text-destructive/80">{evaluation.error}</p>
              </div>
            )}

            {/* Timestamps */}
            <div>
              <h3 className="text-lg font-medium mb-3">Timeline</h3>
              <div className="space-y-2">
                {evaluation.lastRun && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Last Run</span>
                    <span className="text-sm">{formatDate(evaluation.lastRun)}</span>
                  </div>
                )}
                {evaluation.nextRun && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Next Run</span>
                    <span className="text-sm">{formatDate(evaluation.nextRun)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-border flex justify-between">
            <Button
              variant="destructive"
              onClick={() => {
                onClose();
                handleDeleteEvaluation(evaluation.id);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Evaluation
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Evaluations</h1>
            <p className="text-muted-foreground mt-1">
              Manage and monitor your LLM model evaluations
            </p>
          </div>
          <div className="flex space-x-3">
            <Button onClick={() => navigate('/evaluations/wizard')}>
              <Plus className="w-4 h-4 mr-2" />
              New Evaluation
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="evaluations">
              <BarChart3 className="w-4 h-4 mr-2" />
              Evaluations
            </TabsTrigger>
            <TabsTrigger value="runs">
              <Activity className="w-4 h-4 mr-2" />
              Runs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="evaluations">
            {/* Stats Cards for Evaluations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <BarChart3 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Evaluations</p>
                      <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <Calendar className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Scheduled</p>
                      <p className="text-2xl font-bold text-foreground">{stats.scheduled}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Performance</p>
                      <p className="text-2xl font-bold text-foreground">{stats.avgPerformance}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filter for Evaluations */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search evaluations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
                >
                  <option value="all">All Status</option>
                  <option value="running">Running</option>
                  <option value="completed">Completed</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="failed">Failed</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>

            {/* Evaluations List */}
            <Card>
              <CardHeader>
                <CardTitle>Evaluations</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading evaluations...</p>
                  </div>
                ) : filteredEvaluations.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {searchTerm || filterStatus !== 'all' ? 'No matching evaluations' : 'Your evaluations will appear here'}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {searchTerm || filterStatus !== 'all'
                        ? 'Try adjusting your search or filter criteria'
                        : 'Your evaluations will appear here when available'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredEvaluations.map((evaluation) => (
                      <div
                        key={evaluation.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedEvaluation(evaluation);
                          setShowDetailModal(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedEvaluation(evaluation);
                            setShowDetailModal(true);
                          }
                        }}
                        className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-medium text-foreground">
                                {evaluation.name}
                              </h3>
                              {getStatusBadge(evaluation.status)}
                              {getLatestRunBadge(evaluation)}
                              {evaluation.status === 'running' && (
                                <span className="text-sm text-muted-foreground">
                                  {evaluation.progress}% complete
                                </span>
                              )}
                            </div>

                            <p className="text-sm text-muted-foreground mb-3">
                              {evaluation.description}
                            </p>

                            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                              <span>Provider: {evaluation.provider}</span>
                              <span>Model: {evaluation.model}</span>
                              <span>Template: {evaluation.template?.name || evaluation.template || '-'}</span>
                              {evaluation.lastRun && (
                                <span>Last run: {formatDate(evaluation.lastRun)}</span>
                              )}
                            </div>

                            {evaluation.status === 'running' && (
                              <div className="mt-3 w-full bg-secondary rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${evaluation.progress}%` }}
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEvaluation(evaluation);
                                setShowDetailModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>

                            {evaluation.status === 'running' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handlePauseEvaluation(evaluation.id); }}
                              >
                                <Pause className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleRunEvaluation(evaluation.id); }}
                                disabled={evaluation.status === 'running'}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); handleDeleteEvaluation(evaluation.id); }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="runs">
            {/* Stats Cards for Runs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Activity className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Runs</p>
                      <p className="text-2xl font-bold text-foreground">{runs.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Completed</p>
                      <p className="text-2xl font-bold text-foreground">{runs.filter(r => r.status === 'completed').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <RefreshCw className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Running</p>
                      <p className="text-2xl font-bold text-foreground">{runs.filter(r => r.status === 'running').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filter for Runs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search runs..."
                  value={runsSearchTerm}
                  onChange={(e) => setRunsSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                  value={runsFilterStatus}
                  onChange={(e) => setRunsFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
                >
                  <option value="all">All Status</option>
                  <option value="running">Running</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Runs List */}
            <Card>
              <CardHeader>
                <CardTitle>Evaluation Runs</CardTitle>
              </CardHeader>
              <CardContent>
                {runsLoading ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading runs...</p>
                  </div>
                ) : filteredRuns.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {runsSearchTerm || runsFilterStatus !== 'all' ? 'No matching runs' : 'Your evaluation runs will appear here'}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {runsSearchTerm || runsFilterStatus !== 'all'
                        ? 'Try adjusting your search or filter criteria'
                        : 'Run an evaluation to see results here'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredRuns.map((run) => (
                      <div
                        key={run.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedRun(run);
                          setShowRunDetailModal(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedRun(run);
                            setShowRunDetailModal(true);
                          }
                        }}
                        className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-medium text-foreground">
                                {run.evaluation_name || run.evaluation?.name || 'Unknown Evaluation'}
                              </h3>
                              {getStatusBadge(run.status)}
                              <Badge variant="outline">{run.run_id}</Badge>
                              {run.status === 'running' && run.progress && (
                                <span className="text-sm text-muted-foreground">
                                  {run.progress.percentage}% complete
                                </span>
                              )}
                            </div>

                            <div className="flex items-center space-x-6 text-sm text-muted-foreground mb-3">
                              <span>Model: {run.model}</span>
                              <span>Provider: {run.provider}</span>
                              <span>Trigger: {run.trigger}</span>
                              <span>Started: {formatDate(run.started_at)}</span>
                              {(run.overall_score || run.results?.summary?.average_score) && (
                                <span>Score: {Math.round((run.overall_score || run.results?.summary?.average_score) * 100)}%</span>
                              )}
                            </div>

                            {run.status === 'running' && run.progress && (
                              <div className="w-full bg-secondary rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${run.progress.percentage}%` }}
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRun(run);
                                setShowRunDetailModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>

                            {run.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `/evaluations/runs/${run.run_id}`;
                                }}
                              >
                                <BarChart3 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Detail Modals */}
        <EvaluationDetailModal
          evaluation={selectedEvaluation}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedEvaluation(null);
          }}
        />

        <RunDetailModal
          run={selectedRun}
          isOpen={showRunDetailModal}
          onClose={() => {
            setShowRunDetailModal(false);
            setSelectedRun(null);
          }}
        />

        {/* Post-execution modals */}
        <ScheduleModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setModalRunData(null);
          }}
          onSchedule={onScheduleSubmit}
          runData={modalRunData}
        />

        <SendEmailModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setModalRunData(null);
          }}
          onSendEmail={onEmailSubmit}
          runData={modalRunData}
        />

        <SendWebhookModal
          isOpen={showWebhookModal}
          onClose={() => {
            setShowWebhookModal(false);
            setModalRunData(null);
          }}
          onSendWebhook={onWebhookSubmit}
          runData={modalRunData}
        />
      </div>
    </EvaluationLayout>
  );
};

export default EvaluationsDashboard;
